import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CatalogPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const { customer, isLoggedIn } = useCustomer();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categoryId || "disponibili");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("monoporzione"); // "monoporzione" o "gastronomia"

  // Special list state per Lorenzo
  const isSpecialCustomer = isLoggedIn && customer?.is_special_customer;
  const [specialList, setSpecialList] = useState({});
  const [hasActiveList, setHasActiveList] = useState(false);
  const [submittingList, setSubmittingList] = useState(false);

  const isBusiness = isLoggedIn && customer?.account_type === "business" && !customer?.partner_company_id;

  // Funzione per ottenere la quantità nel carrello di un prodotto
  const getCartQuantity = (productId) => {
    const cartItem = cart.find(item => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  useEffect(() => {
    fetchCategories();
  }, [viewMode, isBusiness]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, viewMode]);

  // Check se Lorenzo ha già una lista attiva
  useEffect(() => {
    if (isSpecialCustomer) {
      const token = localStorage.getItem("customer_token");
      axios.get(`${API}/customer/special-list`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data?.items?.length > 0 && !res.data?.completed) {
            setHasActiveList(true);
          }
        })
        .catch(() => {});
    }
  }, [isSpecialCustomer]);

  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [categoryId]);

  const fetchCategories = async () => {
    try {
      const params = {};
      if (viewMode === "gastronomia" && isBusiness) {
        params.include_business = true;
      }
      const response = await axios.get(`${API}/categories`, { params });
      // Filtra in base alla modalità
      let filtered = response.data;
      if (viewMode === "gastronomia") {
        filtered = response.data.filter(c => c.business_only === true);
      } else {
        filtered = response.data.filter(c => !c.business_only);
      }
      setCategories(filtered);
    } catch (error) {
      console.error("Errore nel caricamento delle categorie:", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        is_business_customer: isBusiness
      };
      if (selectedCategory !== "disponibili") {
        params.category_id = selectedCategory;
      }
      if (viewMode === "gastronomia" && isBusiness) {
        params.include_business = true;
      }
      const response = await axios.get(`${API}/products`, { params });
      // Filtra in base alla modalità
      let filtered = response.data;
      if (viewMode === "gastronomia") {
        filtered = response.data.filter(p => p.business_only === true);
      } else {
        filtered = response.data.filter(p => !p.business_only);
      }
      // Se "disponibili", mostra solo prodotti in stock
      if (selectedCategory === "disponibili") {
        filtered = filtered.filter(p => p.stock > 0 && !p.sold_out);
      }
      setProducts(filtered);
    } catch (error) {
      console.error("Errore nel caricamento dei prodotti:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    if (catId === "disponibili") {
      navigate("/catalogo");
    } else {
      navigate(`/catalogo/${catId}`);
    }
  };

  const handleAddToCart = (product) => {
    // Se è Lorenzo e non ha lista attiva, aggiungi alla lista speciale
    if (isSpecialCustomer && !hasActiveList) {
      const currentQty = specialList[product.id]?.quantity || 0;
      const stock = product.stock || 0;
      if (currentQty >= stock) {
        toast.error(`Disponibilità massima raggiunta (${stock})`);
        return;
      }
      setSpecialList(prev => ({
        ...prev,
        [product.id]: {
          product_id: product.id,
          name: product.name,
          price: product.price,
          quantity: currentQty + 1
        }
      }));
      toast.success(`${product.name} aggiunto alla lista`);
      return;
    }

    // Se è Lorenzo con lista attiva, non può aggiungere al carrello
    if (isSpecialCustomer && hasActiveList) {
      toast.info("Vai alla tua lista per fare ordini");
      navigate("/la-mia-lista");
      return;
    }

    const currentInCart = getCartQuantity(product.id);
    const stock = product.stock || 0;
    
    if (currentInCart >= stock) {
      toast.error(`Disponibilità insufficiente! Solo ${stock} pezzi in magazzino.`);
      return;
    }
    
    addToCart(product);
    toast.success(`${product.name} aggiunto al carrello`);
  };

  const specialListItems = Object.values(specialList);
  const specialListTotal = specialListItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const handleConfirmSpecialList = async () => {
    if (specialListItems.length === 0) { toast.error("Aggiungi almeno un prodotto alla lista"); return; }
    setSubmittingList(true);
    try {
      const token = localStorage.getItem("customer_token");
      await axios.post(`${API}/customer/special-list/create`, {
        items: specialListItems
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Lista confermata! I prodotti sono stati riservati.");
      setSpecialList({});
      setHasActiveList(true);
      navigate("/la-mia-lista");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella conferma della lista");
    } finally {
      setSubmittingList(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full" data-testid="catalog-page">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8" data-testid="catalog-title">Catalogo</h1>
        
        {/* Toggle Monoporzione / Gastronomia (solo aziende) */}
        {isBusiness && (
          <div className="mb-6 bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <p className="text-white font-semibold">Scegli il formato:</p>
              <div className="flex bg-black rounded-lg p-1">
                <button
                  onClick={() => { setViewMode("monoporzione"); setSelectedCategory("disponibili"); }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    viewMode === "monoporzione" ? "bg-green-500 text-black" : "text-zinc-400"
                  }`}
                >
                  🍽️ Monoporzione
                </button>
                <button
                  onClick={() => { setViewMode("gastronomia"); setSelectedCategory("disponibili"); }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    viewMode === "gastronomia" ? "bg-green-500 text-black" : "text-zinc-400"
                  }`}
                >
                  🏢 Formati Gastronomia
                </button>
              </div>
            </div>
            {viewMode === "gastronomia" && (
              <div className="mt-3 bg-yellow-500/10 border border-yellow-500 rounded-lg p-3">
                <p className="text-yellow-500 text-sm font-semibold text-center">
                  ⚠️ Sui formati gastronomia non sono applicabili sconti e codici promozionali
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Special Customer Banner */}
        {isSpecialCustomer && hasActiveList && (
          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 flex items-center justify-between">
            <p className="text-yellow-500 font-semibold">Hai già una lista attiva!</p>
            <button onClick={() => navigate("/la-mia-lista")} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-600 text-sm">
              Vai alla Mia Lista
            </button>
          </div>
        )}

        {isSpecialCustomer && !hasActiveList && specialListItems.length > 0 && (
          <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-green-500 font-bold">La tua lista ({specialListItems.length} prodotti)</p>
              <p className="text-white font-bold">€{specialListTotal.toFixed(2)}</p>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {specialListItems.map(item => (
                <span key={item.product_id} className="bg-zinc-800 text-zinc-300 text-xs px-2 py-1 rounded-lg">
                  {item.name} x{item.quantity}
                  <button onClick={() => setSpecialList(prev => { const n = {...prev}; delete n[item.product_id]; return n; })} className="text-red-400 ml-1 hover:text-red-300">x</button>
                </span>
              ))}
            </div>
            <button
              onClick={handleConfirmSpecialList}
              disabled={submittingList}
              className="w-full bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 disabled:opacity-50"
            >
              {submittingList ? "Conferma in corso..." : "Conferma Lista"}
            </button>
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-8 overflow-x-auto" data-testid="category-filter">
          <div className="flex gap-3 pb-2">
            <button
              onClick={() => handleCategoryChange("disponibili")}
              className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all ${
                selectedCategory === "disponibili"
                  ? "bg-green-500 text-black"
                  : "bg-zinc-900 text-white hover:bg-zinc-800"
              }`}
              data-testid="category-disponibili"
            >
              Disponibili
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? "bg-green-500 text-black"
                    : "bg-zinc-900 text-white hover:bg-zinc-800"
                }`}
                data-testid={`category-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-spinner">
            <div className="inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-zinc-400 mt-4">Caricamento prodotti...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12" data-testid="no-products">
            <p className="text-zinc-400 text-lg">Nessun prodotto disponibile in questa categoria.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="products-grid">
            {products.map((product) => {
              // Trova la categoria del prodotto, non quella selezionata
              const productCategory = categories.find(c => c.id === product.category_id);
              const categoryName = productCategory?.name || "";
              // Prodotti gastronomia non sono acquistabili online
              const isGastronomia = viewMode === "gastronomia" || product.business_only;
              
              return (
                <ProductCard 
                  key={product.id}
                  product={product} 
                  onAddToCart={handleAddToCart}
                  categoryName={categoryName}
                  cartQuantity={isSpecialCustomer && !hasActiveList ? (specialList[product.id]?.quantity || 0) : getCartQuantity(product.id)}
                  onClick={() => navigate(`/prodotto/${product.id}`)}
                  isGastronomia={isGastronomia}
                  isBusiness={isBusiness}
                  isSpecialList={isSpecialCustomer && !hasActiveList}
                  isSpecialWithActiveList={isSpecialCustomer && hasActiveList}
                  listQuantity={specialList[product.id]?.quantity || 0}
                />
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CatalogPage;
